import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { formatISO } from 'date-fns'
import { cloneDeep, get, isEmpty, isNil, omit } from 'lodash-es'

import {
  getIsAssignmentGoogleActionPlan,
  getOpenActionPlansForEpisode,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import {
  attendanceValues,
  diagnoses,
} from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionConstants'
import type { endOfSessionMetadataValues } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionMetadata'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import {
  Curriculum,
  Episode,
  EpisodeContents,
  EpisodeStates,
} from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import { TransferStatus } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'

import { actions, mixpanelEvents } from '../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../mixpanel/mixpanelTracking'
import {
  appointmentStatuses,
  getEndOfSessionConditions,
  ProgramConfig,
} from '../../../../common/constants/appConstants'
import { actionAlertHandler, actionStyles } from '../../../../common/constants/reduxConstants'
import { submitRequestPaymentForm } from '../../../../common/http/data/payments'
import { getBCCharge, isEngageDAProvider, logToSentry } from '../../../../common/utils/utils'
import { getAuthUser, getAuthUserId } from '../../../../data/auth/authSelectors'
import { baseApi, RTKQueryTag } from '../../../../data/baseApi'
import { getClientUnclosedPastAppointments } from '../../../../data/lyraTherapy/clientSelectors'
import { getLyraTherapyContentsData } from '../../../../data/lyraTherapy/contentSelectors'
import { useAppDispatch } from '../../../../data/store'
import { useIsEngageDAProvider } from '../../../../hooks/useIsEngageDAProvider'
import { usePreviousRoute } from '../../../../hooks/usePreviousRoute'
import { getProviderDetailsAddresses } from '../../../../providers/data/providerSelectors'
import { submitPaymentRequest } from '../../../../requestPayment/data/requestPaymentActions'
import { getAssignmentProviderPayload } from '../../../assignments/data/assignmentPayload'
import { useSetAssignmentMutation, useUpdateAssignmentMutation } from '../../../assignments/data/assignmentsApi'
import { useAssignmentsData } from '../../../assignments/data/hooks/useAssignmentsData'
import { useCompleteActionPlans } from '../../../assignments/data/hooks/useCompleteActionPlans'
import { useUpdateAppointmentMutation } from '../../../clients/clientDetails/data/appointmentsApi'
import { setAppointment } from '../../../clients/clientDetails/data/appointmentsSlice'
import { useSelectedClientData } from '../../../clients/clientDetails/data/hooks/useSelectedClientData'
import { useUpdateEpisodeMutation } from '../../../episodes/data/episodesApi'
import { getNoShowLateCancelModality } from '../../getNoShowLateCancelModality'

export type CloseSessionSubmitResult = {
  charge: any
  chargeId: string | null
  /** True only when the created charge came back as `pending_note` — i.e. it requires an accept-charge step. */
  isPendingNoteCharge: boolean
  /** True when a charge creation was actually attempted (attended, or DA late-cancel/no-show), regardless of outcome. */
  chargeAttempted: boolean
  shouldCharge: boolean
  hasNoShowTransfersError: boolean
  isCancellationLimitExceeded: boolean
  isClosingOutOfOrder: boolean
}

type UseCloseSessionSubmitParams = {
  appointmentToClose: Appointment | undefined
  selectedEpisode: Episode | undefined
  originallyTargetedAppointment: Appointment | null | undefined
  sessionDetails: { sessionRate: string; sessionType: string } | null
  isUsingSelfPay: boolean
  /** True when the client's customer requires ICD-10 codes for a non health plan (EAP) session, e.g. Solera */
  isCustomerIcd10Required?: boolean
  useEnhancedDiagnosisCapture: boolean
  shouldShowGuardianConsent: boolean
  programConfig: ProgramConfig | undefined
  isDaFlexibleNote?: boolean
}

export const useCloseSessionSubmit = ({
  appointmentToClose,
  selectedEpisode,
  originallyTargetedAppointment,
  sessionDetails,
  isUsingSelfPay,
  isCustomerIcd10Required = false,
  useEnhancedDiagnosisCapture,
  shouldShowGuardianConsent,
  programConfig,
  isDaFlexibleNote = false,
}: UseCloseSessionSubmitParams) => {
  const dispatch = useAppDispatch()
  const [isFormProcessing, setIsFormProcessing] = useState(false)

  const user = useSelector(getAuthUser)
  const userId = useSelector(getAuthUserId)
  const { data: clientDetails } = useSelectedClientData()
  const providerAddresses = useSelector(getProviderDetailsAddresses)
  const pastAppointmentsToClose = useSelector(getClientUnclosedPastAppointments)
  const contents = useSelector(getLyraTherapyContentsData)
  const { data: assignments } = useAssignmentsData()
  const completeActionPlans = useCompleteActionPlans()

  const [updateAppointment] = useUpdateAppointmentMutation()
  const [updateEpisode] = useUpdateEpisodeMutation()
  const [updateAssignment] = useUpdateAssignmentMutation()
  const [setAssignmentMutation] = useSetAssignmentMutation()

  const isProviderEngageDA = useIsEngageDAProvider()
  const prevRoute = usePreviousRoute()
  const conditionsList = useMemo(
    () => getEndOfSessionConditions({ isProviderEngageDA, isDaFlexibleNote }),
    [isProviderEngageDA, isDaFlexibleNote],
  )

  const clientId = clientDetails?.id
  const curriculum = selectedEpisode?.curriculum || []
  const assignmentProviderPayload = getAssignmentProviderPayload(selectedEpisode, user?.id ?? '')

  const isClosingOutOfOrder = useMemo(() => {
    if (
      !isNil(originallyTargetedAppointment) &&
      !isEmpty(originallyTargetedAppointment) &&
      originallyTargetedAppointment.appointmentId !== appointmentToClose?.appointmentId
    ) {
      const nextAppointmentToClose = pastAppointmentsToClose.find((appt: any) => {
        return appt.episodeId === selectedEpisode?.id
      })
      return (
        nextAppointmentToClose && nextAppointmentToClose?.appointmentId !== originallyTargetedAppointment?.appointmentId
      )
    }
    return false
  }, [appointmentToClose?.appointmentId, originallyTargetedAppointment, pastAppointmentsToClose, selectedEpisode?.id])

  const assignCurrentSessionActivities = async () => {
    if (!appointmentToClose || !assignmentProviderPayload) return
    const newSessionCount = appointmentToClose.sessionNumber + 1
    const curriculumObject: Curriculum | undefined = curriculum?.find((obj: any) => obj.session === newSessionCount)
    if (!curriculumObject || isEmpty(curriculumObject?.contents)) return

    const promises: any = []
    curriculumObject.contents.forEach(async (activity: EpisodeContents) => {
      if (activity.group === 'exercise' && activity.id) {
        return
      } else {
        const content = contents.find((content: any) => content.name === activity.name)

        if (!content) return
        promises.push(
          setAssignmentMutation({
            ...assignmentProviderPayload,
            patient_id: clientId as string,
            content_id: content.id as string,
            content_meta_data: (activity.content_meta_data ?? content.meta_data) as any,
            instructions: (activity.instructions ?? content.instructions) as string | undefined,
            recipient_id: activity.recipient_id,
            session_period: newSessionCount,
          }).unwrap(),
        )
      }
    })
    return Promise.all(promises)
  }

  const expireAndCompletePreviousAssignments = async () => {
    if (!appointmentToClose || !assignments || !assignmentProviderPayload) {
      return
    }
    const assignmentsToMarkAsMissed = assignments?.filter((assignment) => {
      return (
        assignment.session_period <= appointmentToClose.sessionNumber &&
        ['new', 'in_progress'].includes(assignment.status) &&
        get(assignment, 'content.stage') !== 'initial' &&
        get(assignment, 'content.group') !== 'exercise'
      )
    })
    const exercisesToMarkAsCompleted = assignments.filter((assignment) => {
      return (
        get(assignment, 'content.group') === 'exercise' &&
        assignment.session_period <= appointmentToClose.sessionNumber &&
        ['in_progress'].includes(assignment.status) &&
        !isEmpty(assignment.assignment_responses) &&
        assignment.assignment_responses?.some((response) => response.status === 'completed') &&
        !getIsAssignmentGoogleActionPlan(assignment)
      )
    })
    const exercisesToMarkAsMissed = assignments.filter((assignment) => {
      return (
        get(assignment, 'content.group') === 'exercise' &&
        assignment.session_period <= appointmentToClose.sessionNumber &&
        (['new'].includes(assignment.status) ||
          (['in_progress'].includes(assignment.status) &&
            !assignment.assignment_responses?.some((response) => response.status === 'completed'))) &&
        !getIsAssignmentGoogleActionPlan(assignment)
      )
    })
    const promises: Promise<any>[] = []
    assignmentsToMarkAsMissed
      .concat(exercisesToMarkAsMissed)
      .forEach((assignment) => promises.push(updateAssignment({ ...assignment, status: 'missed' }).unwrap()))

    exercisesToMarkAsCompleted.forEach((exercise) =>
      promises.push(updateAssignment({ ...exercise, status: 'completed' }).unwrap()),
    )

    return Promise.all(promises)
  }

  const submitForm = async (
    closureFormValues: endOfSessionMetadataValues,
  ): Promise<CloseSessionSubmitResult | undefined> => {
    if (!appointmentToClose) return undefined
    const {
      attendance,
      changeNotice,
      episode_state: episodeState,
      termination_comment: terminationComment,
      technicalIssuesCancelDetails,
      technicalIssuesCancelSelection,
      diagnosis,
      primary_condition,
      providerInitiated,
      providerInitiatedDetails,
      secondary_condition,
      guardianConsent,
      otherChangeReason,
    } = closureFormValues
    const changeReason = closureFormValues.changeReason

    const isNonAttendedSession = attendance === attendanceValues.NO_SHOW
    const isLateClientCancellation =
      changeReason === 'clientInitiatedLate' ||
      (changeReason === 'clientCancel' && changeNotice === 'clientInitiatedLate')

    const { missed, canceled, completed } = appointmentStatuses
    const isAppointmentMissed = isNonAttendedSession
    const appointmentToUpdate = cloneDeep(appointmentToClose)
    const formValues: endOfSessionMetadataValues = cloneDeep(
      omit(closureFormValues, ['diagnosis', 'primary_condition', 'secondary_condition']),
    )
    appointmentToUpdate.appointmentStatus = (
      isAppointmentMissed && changeReason === 'clientInitiatedLate'
        ? missed
        : isAppointmentMissed
          ? canceled
          : completed
    ) as any
    appointmentToUpdate.changeReason = changeReason !== 'clientCancel' ? changeReason : changeNotice

    if (attendance === attendanceValues.INELIGIBLE) {
      appointmentToUpdate.changeReason = 'clientIneligible'
    }

    if (technicalIssuesCancelDetails || technicalIssuesCancelSelection) {
      appointmentToUpdate.appointmentAttributes = { technicalIssuesCancelDetails, technicalIssuesCancelSelection }
    }

    if (changeReason === 'providerInitiated') {
      appointmentToUpdate.appointmentAttributes = {
        providerInitiatedCancelSelection: providerInitiated,
        providerInitiatedCancelDetails: providerInitiatedDetails,
      }
    }

    if (appointmentToUpdate.isChargeReversed) {
      appointmentToUpdate.isChargeReversed = false
    }

    if (changeReason === 'other') {
      appointmentToUpdate.appointmentAttributes = {
        ...appointmentToUpdate.appointmentAttributes,
        otherCancelDetails: otherChangeReason,
      }
    }

    if (useEnhancedDiagnosisCapture) {
      formValues.primary_condition = primary_condition?.map((selectedDiagnosis: string) => {
        const condition = conditionsList.find((possibleDiagnosis) => possibleDiagnosis.label === selectedDiagnosis)
        return condition ? condition.value : ''
      })
      const mappedSecondaryConditions = secondary_condition?.map((selectedDiagnosis: string) => {
        const condition = conditionsList.find((possibleDiagnosis) => possibleDiagnosis.label === selectedDiagnosis)
        return condition ? condition.value : ''
      })
      formValues.secondary_condition =
        mappedSecondaryConditions && mappedSecondaryConditions?.length > 0 ? mappedSecondaryConditions : null
      formValues.diagnosis = []
    } else {
      // @ts-expect-error TS(2339): Property 'diagnosis' does not exist on type 'endOfS... Remove this comment to see the full error message
      closureFormValues.diagnosis = diagnosis?.map((selectedDiagnosis: string) =>
        diagnoses.find((possibleDiagnosis) => possibleDiagnosis.label === selectedDiagnosis),
      )
    }

    try {
      const convertedMeetingTypeAppointment = { ...appointmentToUpdate }
      if (appointmentToUpdate?.meetingFormat === MEETING_FORMATS.IN_PERSON) {
        convertedMeetingTypeAppointment.meetingFormat = MEETING_FORMATS.IN_PERSON_UNDERSCORE
      }
      let charge: any = {}
      let hasNoShowTransfersError = false
      let isCancellationLimitExceeded = false
      setIsFormProcessing(true)

      const shouldCharge = attendance === attendanceValues.ATTENDED
      // Tracks whether we actually hit a charge-creating path (attended here, or DA late-cancel/no-show
      // below). Used by the caller to distinguish "no charge was expected" from "charge creation failed".
      let chargeAttempted = shouldCharge
      if (shouldCharge) {
        charge = await dispatch(
          submitPaymentRequest(
            getBCCharge({
              values: useEnhancedDiagnosisCapture ? formValues : closureFormValues,
              provider: user,
              client: clientDetails,
              appt: convertedMeetingTypeAppointment,
              episode: selectedEpisode,
              meetingFormat: convertedMeetingTypeAppointment.meetingFormat,
              isProviderEngageDA,
              sessionRate: sessionDetails?.sessionRate ?? '0',
              sessionType: sessionDetails?.sessionType ?? 'other',
              isUsingSelfPay,
              isCustomerIcd10Required,
            }),
            true,
            false,
          ),
        )

        logToSentry('closeSession', {
          step: '1. Charge sent',
          clientId,
          returnedCharge: (charge as any).data,
        })
      }

      const isDAEngageProvider = user?.roles && isEngageDAProvider(user.roles)
      let updatedAppointment = {}

      if (isDAEngageProvider && isNonAttendedSession && isLateClientCancellation) {
        chargeAttempted = true
        const noShowSessionFormValues = Object.fromEntries(
          Object.entries(closureFormValues).filter(([, value]) => !isNil(value)),
        )

        if (changeReason === 'clientCancel' && changeNotice === 'clientInitiatedLate') {
          noShowSessionFormValues.attendance = attendanceValues.LAST_MIN_CANCEL
        }

        noShowSessionFormValues.modality = getNoShowLateCancelModality(
          appointmentToUpdate.meetingFormat,
          providerAddresses,
        )

        noShowSessionFormValues.diagnosis = []

        // Awaited so `hasNoShowTransfersError` / `updatedAppointment` are settled before we read them
        // below (and return the flag). A fire-and-forget chain here would always report success.
        try {
          charge = await submitRequestPaymentForm(
            getBCCharge({
              values: noShowSessionFormValues,
              provider: user,
              client: clientDetails,
              appt: convertedMeetingTypeAppointment,
              episode: selectedEpisode,
              meetingFormat: convertedMeetingTypeAppointment.meetingFormat,
              isProviderEngageDA,
              sessionRate: sessionDetails?.sessionRate ?? '0',
              sessionType: sessionDetails?.sessionType ?? 'other',
            }),
          )
          updatedAppointment = await updateAppointment({
            providerId: userId,
            data: appointmentToUpdate,
            showAlert: false,
          }).unwrap()
        } catch (error: any) {
          hasNoShowTransfersError = true
          const errorType = error.response?.data?.type
          if (
            error.response?.status === 400 &&
            (errorType === 'cancellation_policy' || errorType === 'cancellation_policy_health_plan')
          ) {
            isCancellationLimitExceeded = true
            // A cancellation-policy rejection still closes the appointment, so record the update.
            updatedAppointment = await updateAppointment({
              providerId: userId,
              data: appointmentToUpdate,
              showAlert: false,
            }).unwrap()
          }
          actionAlertHandler({
            actionStyle: actionStyles.ERROR,
            message: error.response?.data?.message,
            expires: false,
            dispatch,
          })
        }
      } else {
        updatedAppointment = await updateAppointment({
          providerId: userId,
          data: appointmentToUpdate,
          showAlert: false,
        }).unwrap()
      }

      if (isProviderEngageDA) {
        track({
          event: mixpanelEvents.CONFIRM_CLOSE_SESSION,
          details: {
            appointmentId: appointmentToUpdate?.appointmentId,
          },
        })
      } else {
        track({
          event: mixpanelEvents.BUTTON_PRESS,
          action: actions.CLOSE_SESSION,
          details: {
            appointmentId: appointmentToUpdate?.appointmentId,
            entryPoint: prevRoute?.includes('calendar') ? 'In Product Calendar' : 'Sessions tab',
          },
        })
      }

      logToSentry('closeSession', { step: '2. Appointment updated', clientId, updatedAppointment })
      if (!selectedEpisode) {
        return
      }

      if (shouldShowGuardianConsent && !isProviderEngageDA) {
        await updateEpisode({
          ...selectedEpisode,
          guardian_consent: guardianConsent,
        }).unwrap()
      }

      if (closureFormValues.is_last_session) {
        await updateEpisode({
          ...selectedEpisode,
          state: episodeState as EpisodeStates,
          end_date: formatISO(new Date()),
          end_episode_note: terminationComment,
        }).unwrap()
        // Complete the Action Plan if there is one.
        if (assignmentProviderPayload) {
          try {
            await completeActionPlans(getOpenActionPlansForEpisode(assignments, selectedEpisode.id))
          } catch (actionPlanError) {
            logToSentry(
              'closeSession',
              { step: 'Error completing Action Plan', clientId, errorMessage: actionPlanError?.message },
              'error',
            )
          }
        }
      } else if (!isAppointmentMissed && programConfig?.autoAssignOnSessionClose && !isProviderEngageDA) {
        await expireAndCompletePreviousAssignments()
        logToSentry('closeSession', {
          step: '3. Previous activities completed',
          clientId,
        })
        await assignCurrentSessionActivities()
        logToSentry('closeSession', {
          step: '4. New activites assigned',
          clientId,
        })
      }

      if (
        !isNil(originallyTargetedAppointment) &&
        !isEmpty(originallyTargetedAppointment) &&
        originallyTargetedAppointment.appointmentId !== appointmentToClose.appointmentId &&
        !isProviderEngageDA
      ) {
        const nextAppointmentToClose = pastAppointmentsToClose.find((appt: any) => {
          return appt.episodeId === selectedEpisode?.id
        })
        if (
          nextAppointmentToClose &&
          nextAppointmentToClose?.appointmentId !== originallyTargetedAppointment?.appointmentId
        ) {
          dispatch(setAppointment(nextAppointmentToClose))
        }
      }

      if (!hasNoShowTransfersError && !isProviderEngageDA) {
        if (isClosingOutOfOrder) {
          // Out-of-order success is handled by the component via the returned isClosingOutOfOrder flag
        } else {
          actionAlertHandler({
            actionStyle: actionStyles.SUCCESS,
            message: `Session ${appointmentToClose.sessionNumber} has been closed`,
            expires: false,
            dispatch,
            autoDismissTimer: 4000,
          })
        }
      }

      dispatch(baseApi.util.invalidateTags([{ type: RTKQueryTag.INDIVIDUAL_CLIENT, id: clientId }]))

      const chargeId = get(charge, 'data.data.id') ?? null
      const chargeStatus = get(charge, 'data.data.status') ?? null
      const isPendingNoteCharge = chargeStatus === TransferStatus.PENDING_NOTE

      return {
        charge,
        chargeId,
        isPendingNoteCharge,
        chargeAttempted,
        shouldCharge,
        hasNoShowTransfersError,
        isCancellationLimitExceeded,
        isClosingOutOfOrder: !!isClosingOutOfOrder,
      }
    } catch (error: any) {
      logToSentry(
        'closeSession',
        {
          step: 'Error closing session',
          clientId,
          errorMessage: error?.message,
        },
        'error',
      )
      throw error
    } finally {
      setIsFormProcessing(false)
    }
  }

  return { submitForm, isFormProcessing, isClosingOutOfOrder }
}
